/**
 * Theme - Single source of truth for the game's visual language.
 *
 * Provides the UI font, a cohesive palette, a typographic scale, and reusable
 * rounded button / panel components so every scene looks consistent and reads
 * well on any screen. All sizes are authored against the 720x1280 design
 * canvas; Phaser's FIT scale mode maps them to the device.
 */

import Phaser from 'phaser';
import { AudioManager } from '../game/AudioManager';

export const FONT = "'Outfit', system-ui, -apple-system, 'Segoe UI', sans-serif";

/** Hex string colors for text fills */
export const INK = {
  primary: '#f4f7fb',
  secondary: '#b4c0cf',
  faint: '#7f8da0',
  gold: '#f5b942',
  blue: '#5bb0ef',
  green: '#4fd18b',
  red: '#ef6a5a',
  dark: '#10182a',
} as const;

/** Numeric colors for Phaser fills/strokes */
export const PALETTE = {
  bgTop: 0x202d44,
  bgBottom: 0x141d30,
  panel: 0x26344c,
  panelRaised: 0x32445f,
  panelBorder: 0x415068,
  stroke: 0x415068,
  gold: 0xf5b942,
  goldDark: 0xc8902a,
  blue: 0x3f8fd6,
  blueLight: 0x5bb0ef,
  green: 0x35b877,
  greenLight: 0x4fd18b,
  red: 0xe05a4a,
  redDark: 0xb4422f,
  white: 0xffffff,
  ink: 0x10182a,
  shadow: 0x0a0f1c,
};

export const RARITY = {
  common: '#9aa7b4',
  uncommon: '#54c98a',
  rare: '#4aa3e0',
  legendary: '#f5a623',
} as const;

export const RARITY_HEX = {
  common: 0x9aa7b4,
  uncommon: 0x54c98a,
  rare: 0x4aa3e0,
  legendary: 0xf5a623,
} as const;

type StyleOverrides = Partial<Phaser.Types.GameObjects.Text.TextStyle>;

function style(
  size: number,
  weight: number,
  color: string,
  extra: StyleOverrides = {}
): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontFamily: FONT,
    fontSize: `${size}px`,
    fontStyle: String(weight),
    color,
    ...extra,
  };
}

/**
 * Typographic presets. Call with optional overrides, e.g. T.title({ color: INK.gold }).
 */
export const T = {
  display: (o: StyleOverrides = {}) => style(86, 800, INK.primary, o),
  title: (o: StyleOverrides = {}) => style(50, 700, INK.primary, o),
  heading: (o: StyleOverrides = {}) => style(34, 700, INK.primary, o),
  subheading: (o: StyleOverrides = {}) => style(26, 600, INK.secondary, o),
  body: (o: StyleOverrides = {}) => style(22, 500, INK.secondary, o),
  bodyStrong: (o: StyleOverrides = {}) => style(22, 600, INK.primary, o),
  label: (o: StyleOverrides = {}) => style(19, 600, INK.primary, o),
  button: (o: StyleOverrides = {}) => style(30, 700, INK.primary, o),
  small: (o: StyleOverrides = {}) => style(17, 500, INK.secondary, o),
  micro: (o: StyleOverrides = {}) => style(14, 600, INK.faint, o),
  number: (o: StyleOverrides = {}) => style(24, 700, INK.gold, o),
};

export interface ButtonOptions {
  width?: number;
  height?: number;
  fontSize?: number;
  fill?: number;       // base fill color
  textColor?: string;
  stroke?: number;     // border color
  depth?: number;
  onClick?: () => void;
}

export interface Button {
  container: Phaser.GameObjects.Container;
  setEnabled: (enabled: boolean) => void;
  setLabel: (text: string) => void;
}

/**
 * A rounded, tactile button with press feedback. Returns a container plus
 * helpers. The whole control is one hit target sized for touch.
 */
export function makeButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  opts: ButtonOptions = {}
): Button {
  const w = opts.width ?? 320;
  const h = opts.height ?? 78;
  const fill = opts.fill ?? PALETTE.blue;
  const stroke = opts.stroke ?? lighten(fill, 0.25);
  const radius = Math.min(18, h / 2);

  const container = scene.add.container(x, y);
  container.setDepth(opts.depth ?? 100);

  const shadow = scene.add.graphics();
  shadow.fillStyle(PALETTE.shadow, 0.35);
  shadow.fillRoundedRect(-w / 2, -h / 2 + 5, w, h, radius);

  const g = scene.add.graphics();
  const draw = (f: number, s: number) => {
    g.clear();
    g.fillStyle(f, 1);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, radius);
    g.lineStyle(2, s, 1);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, radius);
    // subtle top highlight
    g.fillStyle(PALETTE.white, 0.06);
    g.fillRoundedRect(-w / 2 + 3, -h / 2 + 3, w - 6, h / 2 - 3, radius - 2);
  };
  draw(fill, stroke);

  const text = scene.add.text(0, 0, label, T.button({
    fontSize: `${opts.fontSize ?? 30}px`,
    color: opts.textColor ?? INK.primary,
  }));
  text.setOrigin(0.5);

  container.add([shadow, g, text]);
  container.setSize(w, h);
  container.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);

  let enabled = true;
  let down = false;

  container.on('pointerover', () => {
    if (!enabled) return;
    draw(lighten(fill, 0.12), lighten(stroke, 0.15));
  });
  container.on('pointerout', () => {
    if (!enabled) return;
    down = false;
    draw(fill, stroke);
    container.y = y;
  });
  container.on('pointerdown', () => {
    if (!enabled) return;
    down = true;
    container.y = y + 3;
    draw(darken(fill, 0.1), stroke);
  });
  container.on('pointerup', () => {
    if (!enabled || !down) return;
    down = false;
    container.y = y;
    draw(fill, stroke);
    AudioManager.getInstance().play('click');
    opts.onClick?.();
  });

  return {
    container,
    setEnabled(on: boolean) {
      enabled = on;
      container.setAlpha(on ? 1 : 0.45);
      if (on) container.setInteractive(); else container.disableInteractive();
    },
    setLabel(t: string) {
      text.setText(t);
    },
  };
}

export interface OptionCardOptions {
  width?: number;
  height?: number;
  icon?: string;        // emoji/glyph shown on the left
  iconColor?: string;
  accent?: number;      // border + name color
  accentInk?: string;
  enabled?: boolean;
  note?: string;        // small note shown when disabled
  depth?: number;
  onClick?: () => void;
}

/**
 * A tappable option card: left icon, bold name, wrapped description. Used by
 * the reward / shop / heal / treasure scenes for a consistent look.
 */
export function makeOptionCard(
  scene: Phaser.Scene,
  x: number,
  y: number,
  name: string,
  description: string,
  opts: OptionCardOptions = {}
): Phaser.GameObjects.Graphics {
  const w = opts.width ?? 620;
  const h = opts.height ?? 130;
  const accent = opts.accent ?? PALETTE.blue;
  const accentInk = opts.accentInk ?? '#5bb0ef';
  const enabled = opts.enabled ?? true;
  const depth = opts.depth ?? 100;

  const panel = makePanel(scene, x, y, w, h, { fill: PALETTE.panel, stroke: accent, strokeWidth: 3, radius: 18 });
  panel.setDepth(depth);
  panel.setAlpha(enabled ? 1 : 0.5);

  const hasIcon = !!opts.icon;
  const textLeft = hasIcon ? x - w / 2 + 130 : x - w / 2 + 30;

  if (hasIcon) {
    const icon = scene.add.text(x - w / 2 + 70, y, opts.icon!, {
      fontFamily: FONT, fontSize: '52px', color: opts.iconColor ?? accentInk,
    });
    icon.setOrigin(0.5);
    icon.setDepth(depth + 1);
    icon.setAlpha(enabled ? 1 : 0.5);
  }

  const nameText = scene.add.text(textLeft, y - 26, name, T.heading({ color: accentInk }));
  nameText.setOrigin(0, 0.5);
  nameText.setDepth(depth + 1);
  nameText.setAlpha(enabled ? 1 : 0.5);

  const descText = scene.add.text(textLeft, y + 22, description, T.body({
    color: INK.secondary, wordWrap: { width: w - (textLeft - (x - w / 2)) - 40 }, lineSpacing: 2,
  }));
  descText.setOrigin(0, 0.5);
  descText.setDepth(depth + 1);
  descText.setAlpha(enabled ? 1 : 0.5);

  if (!enabled && opts.note) {
    const note = scene.add.text(x + w / 2 - 24, y + h / 2 - 18, opts.note, T.micro({ color: INK.faint }));
    note.setOrigin(1, 1);
    note.setDepth(depth + 1);
  }

  if (enabled) {
    const hit = scene.add.rectangle(x, y, w, h, 0x000000, 0.001);
    hit.setInteractive({ useHandCursor: true });
    hit.setDepth(depth + 2);
    hit.on('pointerover', () => panel.setAlpha(0.82));
    hit.on('pointerout', () => panel.setAlpha(1));
    hit.on('pointerdown', () => opts.onClick?.());
  }

  return panel;
}

export interface PanelOptions {
  fill?: number;
  stroke?: number;
  radius?: number;
  alpha?: number;
  strokeWidth?: number;
  depth?: number;
}

/**
 * A rounded panel background. Returns the Graphics so callers can place content
 * on top. Drawn centered at (x, y).
 */
export function makePanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  opts: PanelOptions = {}
): Phaser.GameObjects.Graphics {
  const radius = opts.radius ?? 20;
  const g = scene.add.graphics();
  g.fillStyle(opts.fill ?? PALETTE.panel, opts.alpha ?? 1);
  g.fillRoundedRect(x - w / 2, y - h / 2, w, h, radius);
  g.lineStyle(opts.strokeWidth ?? 2, opts.stroke ?? PALETTE.stroke, 1);
  g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, radius);
  if (opts.depth !== undefined) g.setDepth(opts.depth);
  return g;
}

/**
 * Paint a vertical gradient background across the whole design canvas.
 * Cheap (a few stacked rects) and consistent across scenes.
 */
export function paintBackground(scene: Phaser.Scene, width: number, height: number): void {
  const g = scene.add.graphics();
  const steps = 32;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const color = blend(PALETTE.bgTop, PALETTE.bgBottom, t);
    g.fillStyle(color, 1);
    g.fillRect(0, Math.floor((i / steps) * height), width, Math.ceil(height / steps) + 1);
  }
  g.setDepth(-1000);
}

/**
 * Smooth fade-in for a scene's camera, from the theme background color (not
 * harsh black). Call at the top of create() for a consistent, snappy
 * transition into every screen.
 */
export function fadeInScene(scene: Phaser.Scene, ms = 240): void {
  scene.cameras.main.fadeIn(ms, 20, 28, 44);
}

/**
 * Fade the camera out, then start the next scene (which fades itself in).
 * Use for the big, deliberate transitions (entering/leaving a battle).
 */
export function fadeToScene(scene: Phaser.Scene, key: string, data?: object, ms = 220): void {
  const cam = scene.cameras.main;
  cam.fadeOut(ms, 20, 28, 44);
  cam.once('camerafadeoutcomplete', () => scene.scene.start(key, data));
}

// ---- color helpers ----

export function lighten(color: number, amount: number): number {
  return blend(color, 0xffffff, amount);
}
export function darken(color: number, amount: number): number {
  return blend(color, 0x000000, amount);
}
export function blend(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}
export function hexStr(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}
